import SettingsIcon from '@mui/icons-material/Settings';
import {
	Box,
	Button,
	Divider,
	IconButton,
	List,
	MenuList,
	MenuItem as MuiMenuItem,
	Paper,
	Popper,
	Stack,
	SvgIcon,
	Typography,
} from '@mui/material';
import config from 'config';
import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { setCurrentConversationId } from '../../../actions/aiAssistant.actions';
import { borderRadius, customSizes, palette, spaces } from '../../../assets/styles/themes/tokens';
import { ampliConversionPath, externalLinks, routes } from '../../../constants';
import { getSubscription, selectUser } from '../../../reducers/user.selectors';
import services from '../../../services';
import { aiAssistantContext } from '../../../state/AIAssistantContextProvider';
import { aiAssistantEditorContext } from '../../../state/AIAssistantEditorProvider';
import getMuiIcon from '../../../utils/getMuiIcon';
import { isSchoolSubdomain } from '../../../utils/subdomain';
import useAction from '../../../utils/useAction';
import useHomeNavigation from '../../../utils/useHomeNavigation';
import useRouter from '../../../utils/useRouter';
import useShouldShowAIAssistant from '../../../utils/useShouldShowAIAssistant';
import { ActiveView } from '../../AIAssistant/shared/constants';
import Logout from '../../Logout';
import TrialReportCardCard from '../../TrialReportCard/TrialReportCardCard';
import CustomSubList from './CustomSubList';
import MenuItem from './MenuItem';
import MenuItemTooltip from './MenuItemTooltip';
import MuLogo from './MuLogo';
import {
	getAdminMenuItems,
	getCourseMenuItems,
	getLibraryMenuItems,
	getMukibudeMenuItems,
	getSettingsMenuItems,
	getSupportMenuItems,
} from './sidebarMenuConfig';

const interactionStyles = {
	'&:hover': {
		backgroundColor: palette.primary.selected,
	},
	'&:focus': {
		backgroundColor: 'transparent',
		outline: 'none',
	},
	'&:focus:hover': {
		backgroundColor: palette.primary.selected,
	},
	'&:active': {
		backgroundColor: palette.common.white,
	},
};

export const getNavWrapperStyles = (isCollapsed: boolean) => ({
	width: isCollapsed ? customSizes.sideNavbar.collapsedWidth : customSizes.sideNavbar.expandedWidth,
	display: 'flex',
	flexDirection: 'column',
	alignItems: isCollapsed ? 'center' : 'flex-start',
	height: '100%',
	position: 'relative',
	backgroundColor: palette.background.paperElevation2,
	'&::after': {
		content: '""',
		position: 'absolute',
		right: 0,
		top: 0,
		bottom: 0,
		width: '1px',
		backgroundColor: palette.elevation.outlined,
	},
});

const getHeaderStyles = (isDrawer: boolean) => ({
	borderBottom: `1px solid ${palette.elevation.outlined}`,
	width: '100%',
	minHeight: isDrawer
		? customSizes.sideNavbar.headerHeight.mobile
		: customSizes.sideNavbar.headerHeight.desktop,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
});

const getListStyles = (isCollapsed: boolean) => ({
	padding: spaces.xs,
	flexGrow: 1,
	width: '100%',
	gap: isCollapsed ? spaces.xxs : spaces.sm,
	display: 'flex',
	flexDirection: 'column',
	overflowY: 'auto',
	'&::-webkit-scrollbar': {
		display: 'none',
	},
	msOverflowStyle: 'none', // IE and Edge
	scrollbarWidth: 'none', // Firefox
});

const closeButtonStyles = {
	borderLeft: `1px solid ${palette.elevation.outlined}`,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	height: '100%',
};

const upgradeButtonWrapperStyles = {
	width: '100%',
	padding: spaces.xs,
	paddingTop: spaces.md,
};

const upgradeButtonCollapsedStyles = {
	width: '100%',
	height: '32px',
	aspectRatio: 1,
	borderRadius: borderRadius.xs,
	border: `1px solid ${palette.primary.outlinedBorder}`,
};

const upgradeButtonIconStyles = {
	width: '20px',
	height: '20px',
};

const footerStyles = {
	paddingX: spaces.md,
	width: '100%',
	borderTop: `1px solid ${palette.elevation.outlined}`,
	height: customSizes.sideNavbar.headerHeight.desktop,
};

const menuCollapsedStyles = {
	borderRadius: borderRadius.md,
	padding: spaces.xs,
	display: 'flex',
	flexDirection: 'column',
};

const popperStyles = {
	position: 'fixed',
	paddingLeft: spaces.xs,
	paddingBottom: spaces.xs,
};

const menuItemCollapsedStyles = {
	cursor: 'pointer',
};

const paragraphIconWrapperStyles = {
	width: customSizes.sideNavbar.collapsedWidth,
	height: customSizes.sideNavbar.collapsedWidth,
	borderTop: `1px solid ${palette.elevation.outlined}`,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
};

const paragraphIconButtonStyles = {
	width: '100%',
	height: '100%',
	'& svg': {
		color: palette.text.secondary,
	},
	'&:hover': {
		backgroundColor: palette.primary.hover,
	},
};

const linkStyles = {
	textDecoration: 'none',
	color: 'inherit',
};

const menuItemStyles = {
	paddingX: spaces.md,
	paddingY: spaces.xs,
};

interface SideNavbarProps {
	isCollapsed: boolean;
	isDrawer?: boolean;
	onCloseDrawer?: () => void;
}

const SideNavbar: React.FC<SideNavbarProps> = ({
	isCollapsed,
	isDrawer = false,
	onCloseDrawer = () => {},
}) => {
	const { t } = useTranslation();
	const { router, match } = useRouter();
	const navigateHome = useHomeNavigation();
	const user = useSelector(selectUser);
	const subscription = useSelector(getSubscription);

	const anchorEl = React.useRef<HTMLButtonElement>(null);
	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

	const pathname = match?.location?.pathname || '';
	const isReaderPage = pathname.includes(`/${routes.reader.root}/`);
	const feedbackUrlParams = new URLSearchParams({
		page_url: pathname,
		document_id: isReaderPage ? match?.params?.documentId : '',
		user_id: user?.id ?? '',
	}).toString();
	const feedbackUrl = `${config.newNavigationFeedback.url}#${feedbackUrlParams}`;
	const { setActiveView, setSelectedTemplatePromptId } = useContext(aiAssistantContext);
	// No-op default outside the AI Tools page's provider.
	const { closeTextEditor } = useContext(aiAssistantEditorContext);
	const setCurrentConversationIdAction = useAction(setCurrentConversationId);
	const adminPaths: string[] = [
		'/school/dashboard',
		'/school/teachers',
		'/school/license',
		'/school/admins',
	];

	const currentPath = match.location.pathname;
	const isOnAdminPage = adminPaths.some(
		(path) => currentPath === path || currentPath.startsWith(path + '/')
	);

	const handleUpdatePlanClick = () => {
		services.track.ampliTrackPricingPlansViewedEvent(
			ampliConversionPath.NavigationBar,
			subscription?.status
		);

		// Only users with a Stripe customer record (an existing or previously
		// cancelled Stripe subscription) take the new in-app upgrade flow. A
		// basis user without `payment.stripeCustomerId` has never been a Stripe
		// customer — they keep the legacy `/premium` Zuora upgrade flow.
		if (user?.isStripeUser) {
			router.push(`/${routes.stripeUpgrade.root}`);
			return;
		}
		router.push(`/${routes.upgradePackageSelection.root}`);
	};

	const isBibliothekUser = !!(user && user.isBibliothekUser);

	const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

	const navWrapperStyles = useMemo(() => getNavWrapperStyles(isCollapsed), [isCollapsed]);

	const headerStyles = useMemo(() => getHeaderStyles(isDrawer), [isDrawer]);

	const NavLink = ({
		to,
		children,
		ariaLabel,
	}: {
		to: string;
		children: React.ReactNode;
		ariaLabel?: string;
	}) => (
		<a href={to} target="_blank" rel="noopener noreferrer" style={linkStyles} aria-label={ariaLabel}>
			{children}
		</a>
	);

	const getUpgradeActionText = () => {
		if (user?.isBasisUser) {
			return t('Upgrade Plan');
		}
		if (user?.isBibliothekUser || user?.isCourseUser) {
			return t('Update Plan');
		}
		if (user?.isKombiUser) {
			return t('Go to school licence');
		}
		return t('Upgrade Plan');
	};

	const adminMenuItems = getAdminMenuItems();

	const libraryMenuItems = getLibraryMenuItems();
	const handleAIToolsClick = () => {
		// Close the editor while the old location is still current (mirrors
		// useBackToAIToolsMain) — the deferred cross-conversation close would
		// otherwise strip the URL against a location the click is navigating away from.
		closeTextEditor();
		setActiveView(ActiveView.FREE_DRAFT_ENTRY);
		setSelectedTemplatePromptId(null);
		setCurrentConversationIdAction({ conversationId: null });
	};
	const mukibudeMenuItems = getMukibudeMenuItems(handleAIToolsClick);
	const shouldShowMukibudeSection = useShouldShowAIAssistant();

	const courseMenuItems = getCourseMenuItems({
		isSchulenUser: isSchoolSubdomain(window.location.hostname),
	});

	const supportMenuItems = getSupportMenuItems({ feedbackUrl });

	const settingsMenuItems = getSettingsMenuItems();

	const schoolAdminSection = () => {
		const wrapperSx = {
			position: 'relative',
			backgroundColor: palette.action.hover,
			borderRadius: borderRadius.sm,
			border: '1px solid',
			borderColor: palette.elevation.outlined,
			margin: isCollapsed ? 0 : spaces.xs,
			padding: isCollapsed ? 0 : spaces.xs,
		};
		return (
			<Box sx={wrapperSx}>
				<CustomSubList
					items={adminMenuItems}
					isCollapsed={isCollapsed}
					sectionTitle="Admin Area"
					highlighted={false}
					hideSubheaderWhenCollapsed
				/>
				{!isCollapsed && (
					<SettingsIcon
						fontSize="small"
						sx={{
							position: 'absolute',
							top: spaces.xxxs,
							right: spaces.md,
							marginTop: spaces.xxs,
							color: palette.text.primary,
						}}
					/>
				)}
			</Box>
		);
	};

	return (
		<Box
			component="nav"
			data-testid="side-navbar"
			sx={{
				...navWrapperStyles,
				position: 'relative',
				height: '100%',
			}}
		>
			<Box sx={headerStyles}>
				<MuLogo
					variant={isCollapsed ? 'small' : 'full'}
					onClick={navigateHome}
					sx={{
						minHeight: isDrawer
							? customSizes.sideNavbar.headerHeight.mobile
							: customSizes.sideNavbar.headerHeight.desktop,
						width: '100%',
					}}
				/>
				{isDrawer && (
					<Box sx={closeButtonStyles}>
						<IconButton
							onClick={onCloseDrawer}
							data-testid="close-button"
							aria-label={t('Close navigation')}
							sx={{
								padding: spaces.md,
								borderRadius: 0,
								...interactionStyles,
							}}
						>
							<SvgIcon
								component={getMuiIcon('close')}
								sx={{ color: palette.text.primary }}
								aria-hidden="true"
							/>
						</IconButton>
					</Box>
				)}
			</Box>
			<List data-testid="side-navbar-menu" sx={getListStyles(isCollapsed)}>
				{!user?.isAiOnlyUser && (
					<MenuItem icon="home" label="Home" to={routes.dashboard.root} isCollapsed={isCollapsed} />
				)}
				{!user?.isAiOnlyUser &&
				user?.isSchoolAdmin &&
				!user?.isStripeUser &&
				isOnAdminPage &&
				adminMenuItems.length > 0
					? schoolAdminSection()
					: null}
				{!user?.isAiOnlyUser && (
					<CustomSubList items={libraryMenuItems} isCollapsed={isCollapsed} sectionTitle="Library" />
				)}
				{shouldShowMukibudeSection && (
					<CustomSubList
						items={mukibudeMenuItems}
						isCollapsed={isCollapsed}
						sectionTitle="Mukibude"
						hideSubheaderWhenCollapsed={!!user?.isAiOnlyUser}
					/>
				)}
				{!user?.isAiOnlyUser && courseMenuItems.length > 0 && (
					<CustomSubList items={courseMenuItems} isCollapsed={isCollapsed} sectionTitle="Shulflix" />
				)}
				<CustomSubList items={supportMenuItems} isCollapsed={isCollapsed} sectionTitle="Support" />
				{isDrawer && (
					<>
						<CustomSubList items={settingsMenuItems} isCollapsed={isCollapsed} sectionTitle="Settings" />
						<Box sx={{ padding: `0 ${spaces.sm}` }}>
							<Divider />
						</Box>
						<Logout
							render={(handleLogout) => (
								<MenuItem icon="logout" label="Logout" onClick={handleLogout} isCollapsed={isCollapsed} />
							)}
						/>
						{/* In the drawer the card scrolls with the menu instead of staying pinned below it. */}
						<TrialReportCardCard isCollapsed={isCollapsed} />
					</>
				)}
			</List>
			{!isDrawer && <TrialReportCardCard isCollapsed={isCollapsed} />}
			{user?.canUpgrade && (
				<Box sx={upgradeButtonWrapperStyles}>
					{isCollapsed ? (
						<MenuItemTooltip title={t('Upgrade plan')}>
							<IconButton
								data-testid="upgrade-plan-icon-button"
								sx={upgradeButtonCollapsedStyles}
								onClick={handleUpdatePlanClick}
								aria-label={getUpgradeActionText()}
							>
								<SvgIcon
									sx={upgradeButtonIconStyles}
									component={getMuiIcon('arrowUpOutline')}
									color="primary"
									aria-hidden="true"
								/>
							</IconButton>
						</MenuItemTooltip>
					) : (
						<Button
							startIcon={<SvgIcon component={getMuiIcon('arrowUpOutline')} />}
							variant={isBibliothekUser ? 'outlined' : 'contained'}
							size="medium"
							color="primary"
							fullWidth
							onClick={handleUpdatePlanClick}
						>
							{getUpgradeActionText()}
						</Button>
					)}
				</Box>
			)}
			{isCollapsed ? (
				<>
					<Popper open={isMenuOpen} anchorEl={anchorEl.current} placement="right" sx={popperStyles}>
						<Paper elevation={8} sx={menuCollapsedStyles}>
							<MenuList disablePadding>
								<MuiMenuItem sx={menuItemStyles}>
									<NavLink to={externalLinks.imprint} ariaLabel={t('Imprint')}>
										<Typography variant="body1" sx={menuItemCollapsedStyles}>
											{t('Imprint')}
										</Typography>
									</NavLink>
								</MuiMenuItem>
								<MuiMenuItem sx={menuItemStyles}>
									<NavLink to={externalLinks.dataPolicy} ariaLabel={t('Privacy')}>
										<Typography variant="body1" sx={menuItemCollapsedStyles}>
											{t('Privacy')}
										</Typography>
									</NavLink>
								</MuiMenuItem>
								<MuiMenuItem sx={menuItemStyles}>
									<NavLink to={externalLinks.tac} ariaLabel={t('Terms')}>
										<Typography variant="body1" sx={menuItemCollapsedStyles}>
											{t('Terms')}
										</Typography>
									</NavLink>
								</MuiMenuItem>
							</MenuList>
						</Paper>
					</Popper>
					<MenuItemTooltip title={t('Legal Information')}>
						<Box sx={paragraphIconWrapperStyles}>
							<IconButton
								data-testid="paragraph-icon"
								ref={anchorEl}
								onClick={toggleMenu}
								sx={paragraphIconButtonStyles}
								aria-label={t('Legal Information')}
								aria-expanded={isMenuOpen}
								aria-haspopup="true"
							>
								<SvgIcon
									component={getMuiIcon('paragraph')}
									color="secondary"
									aria-hidden="true"
									sx={{ width: '16px', height: '16px' }}
								/>
							</IconButton>
						</Box>
					</MenuItemTooltip>
				</>
			) : (
				<Stack direction="row" sx={footerStyles} useFlexGap spacing={spaces.md} alignItems="center">
					<NavLink to={externalLinks.imprint} ariaLabel={t('Imprint')}>
						<Typography variant="caption">{t('Imprint')}</Typography>
					</NavLink>
					<NavLink to={externalLinks.dataPolicy} ariaLabel={t('Privacy')}>
						<Typography variant="caption">{t('Privacy')}</Typography>
					</NavLink>
					<NavLink to={externalLinks.tac} ariaLabel={t('Terms')}>
						<Typography variant="caption">{t('Terms')}</Typography>
					</NavLink>
				</Stack>
			)}
		</Box>
	);
};

export default SideNavbar;
